import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Voice input for the client's composer — the mic beside the reply box.
 *
 * **The browser transcribes, not Complao.** This is the Web Speech API, chosen on 31 August
 * 2026 over routing the audio through Amazon Transcribe beside the platform's own Bedrock,
 * S3 and SES clients. ⚠ In Chrome and Edge the captured audio is streamed to the browser
 * vendor's own service to be recognised, so a client's spoken answer about their management
 * system leaves the per-audit confidentiality boundary that `audits/<Organisation> - <audit>/`
 * draws around everything else they send. That was raised and decided; do not describe it in
 * the interface as private, and do not quietly add a second engine beside it.
 *
 * **Nothing is recorded.** No clip is captured, buffered or uploaded — the recognised text
 * lands in the composer and the audio has no existence Complao can observe. The record of
 * the session stays what it has always been: the words the client read back and sent. Adding
 * a stored clip would be a second evidence shelf, which ADR 0025 does not describe.
 *
 * **The transcript is a draft, never a reply.** Final text is appended to whatever is in the
 * box and the client presses Send themselves. Speech recognition mishears, and an answer
 * auto-sent on a pause in speech would put a sentence the client never approved into a
 * transcript an accreditation assessor reads.
 */

// The Web Speech API is absent from TypeScript's DOM library at 5.9, so the part of it this
// file touches is declared here — narrowly, and as what is actually read, rather than as a
// whole vendor surface nothing else uses.
type SpeechAlternative = { readonly transcript: string };

type SpeechResult = {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechAlternative | undefined;
};

type SpeechResultList = {
  readonly length: number;
  item(index: number): SpeechResult | undefined;
};

type SpeechResultEvent = {
  readonly resultIndex: number;
  readonly results: SpeechResultList;
};

type SpeechErrorEvent = { readonly error: string };

type SpeechRecogniser = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecogniserConstructor = new () => SpeechRecogniser;

/** Safari and older Chrome carry it prefixed; Firefox carries it not at all. */
function recogniserConstructor(): SpeechRecogniserConstructor | null {
  if (typeof window === "undefined") return null;
  const scope = window as unknown as {
    SpeechRecognition?: SpeechRecogniserConstructor;
    webkitSpeechRecognition?: SpeechRecogniserConstructor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

/**
 * The audit is conducted in English, so the recogniser is told so rather than left to read
 * the browser's own locale — a client whose machine is set to another language would
 * otherwise have an English answer transcribed against the wrong acoustic model and get back
 * something that reads as nonsense in the record.
 */
const AUDIT_LANGUAGE = "en-GB";

/**
 * A recogniser that ends the moment it starts is a device or a permission failing, not a
 * silence. Restarting it in a tight loop would spin the browser, so three of those in a row
 * stop the mic and say so.
 */
const IMMEDIATE_END_MS = 400;
const IMMEDIATE_END_LIMIT = 3;

const REFUSALS: Record<string, string> = {
  "not-allowed":
    "Complao could not use the microphone. Allow microphone access for this site in your browser, then press the mic again.",
  "service-not-allowed":
    "Your browser refused the transcription service. Type your answer instead, or try the mic again.",
  "audio-capture":
    "No microphone was found. Check the device is connected and selected, then press the mic again.",
  network:
    "The transcription service could not be reached. Type your answer instead, or try the mic again.",
};

export type Dictation = {
  /** False in Firefox, and on anything else without the API. The button says so. */
  supported: boolean;
  recording: boolean;
  /** What is being said right now, not yet finalised. Shown, never inserted. */
  interim: string;
  problem: string | null;
  toggle: () => void;
  stop: () => void;
};

/**
 * `onText` receives finalised phrases as they settle, one at a time. It is called with the
 * text alone — where it goes in the box is the composer's business, and appending through a
 * functional update is what keeps a client who types while dictating from losing what they
 * typed.
 *
 * `blocked` is the composer's own locked state. A pause, a close, or Complao taking the turn
 * arrives over the socket rather than from anything the client did, so the mic has to be
 * stopped from here — a live recogniser appending into a disabled box would transcribe an
 * answer to a question that is no longer open.
 */
export function useDictation({
  onText,
  blocked,
}: {
  onText: (text: string) => void;
  blocked: boolean;
}): Dictation {
  const [supported] = useState(() => recogniserConstructor() !== null);
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const recogniser = useRef<SpeechRecogniser | null>(null);
  // What the client asked for, as opposed to what the recogniser is doing. Chrome ends a
  // session by itself after a few seconds of silence even with `continuous` set, and the
  // client has not stopped speaking just because they paused to think — so an end that
  // nobody asked for is restarted rather than reported.
  const wanted = useRef(false);
  const startedAt = useRef(0);
  const immediateEnds = useRef(0);
  const sink = useRef(onText);
  sink.current = onText;

  const stop = useCallback(() => {
    wanted.current = false;
    setInterim("");
    recogniser.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Recogniser = recogniserConstructor();
    if (!Recogniser || wanted.current) return;
    setProblem(null);
    setInterim("");
    immediateEnds.current = 0;
    wanted.current = true;

    const instance = new Recogniser();
    instance.lang = AUDIT_LANGUAGE;
    instance.continuous = true;
    instance.interimResults = true;
    instance.maxAlternatives = 1;

    instance.onresult = (event) => {
      // Only what arrived on this event — everything before `resultIndex` has already been
      // read and inserting it twice would double the sentence in the box.
      let settled = "";
      let pending = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results.item(index);
        const heard = result?.item(0)?.transcript ?? "";
        if (!heard) continue;
        if (result?.isFinal) settled += heard;
        else pending += heard;
      }
      if (settled.trim()) {
        immediateEnds.current = 0;
        sink.current(settled.trim());
      }
      setInterim(pending.trim());
    };

    instance.onerror = (event) => {
      // Silence is not a failure and neither is the client pressing stop: both end the
      // recogniser through `onend`, which decides what happens next.
      if (event.error === "no-speech" || event.error === "aborted") return;
      wanted.current = false;
      setProblem(
        REFUSALS[event.error] ??
          "Voice input stopped unexpectedly. Type your answer instead, or try the mic again.",
      );
    };

    instance.onend = () => {
      setInterim("");
      const immediate = Date.now() - startedAt.current < IMMEDIATE_END_MS;
      immediateEnds.current = immediate ? immediateEnds.current + 1 : 0;
      if (wanted.current && immediateEnds.current >= IMMEDIATE_END_LIMIT) {
        wanted.current = false;
        setProblem(
          "The microphone stopped as soon as it started. Check it is connected and permitted, then press the mic again.",
        );
      }
      if (!wanted.current) {
        recogniser.current = null;
        setRecording(false);
        return;
      }
      try {
        startedAt.current = Date.now();
        instance.start();
      } catch {
        // Restarting can throw if the browser has already torn the session down. Nothing is
        // lost — every finalised phrase is in the box — so the mic simply goes quiet.
        wanted.current = false;
        recogniser.current = null;
        setRecording(false);
      }
    };

    recogniser.current = instance;
    try {
      startedAt.current = Date.now();
      instance.start();
      setRecording(true);
    } catch {
      wanted.current = false;
      recogniser.current = null;
      setProblem("Voice input could not be started. Type your answer instead.");
    }
  }, []);

  const toggle = useCallback(() => {
    if (wanted.current) stop();
    else start();
  }, [start, stop]);

  useEffect(() => {
    if (blocked && wanted.current) stop();
  }, [blocked, stop]);

  // Leaving the screen with the mic live would keep the microphone open on a session the
  // client is no longer in. `abort` rather than `stop`: there is nothing left to finalise
  // into, because the composer has gone.
  useEffect(
    () => () => {
      wanted.current = false;
      recogniser.current?.abort();
      recogniser.current = null;
    },
    [],
  );

  return { supported, recording, interim, problem, toggle, stop };
}
